// src/components/NavigationBar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import '../styles/NavigationBar.css';
import logo from '../assets/logo.png';
import EventsDropdown from './EventsDropdown';

const NAV_LINKS_PRIMARY = [
  { to: '/about',        label: 'About' },
  { to: '/menu',         label: 'Menu' },
  { to: '/reservation',  label: 'Reservation' },
  { to: 'https://lebuathai.oddle.me/en_SG', label: 'Delivery', external: true },
];

const NAV_LINKS_SECONDARY = [
  { to: '/promotion',    label: 'Promotions' },
  { to: '/gallery',      label: 'Gallery' },
  { to: '/testimonials', label: 'Reviews' },
  { to: '/contact',      label: 'Contact' },
];

const NavigationBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const location = useLocation();

  // Scroll detection for transparent → solid navbar
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close overlay on route change
  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const isHome = location.pathname === '/';
  const navSolid = !isHome || scrolled;

  return (
    <>
      <nav
        className={`navbar${navSolid ? ' scrolled' : ''}${isMenuOpen ? ' menu-open' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <Link to="/" aria-label="Lebua Thai — Home">
          <img src={logo} alt="Lebua Thai" className="navbar__logo" />
        </Link>

        {/* Desktop links */}
        <ul className="navbar__links">
          {NAV_LINKS_PRIMARY.map(({ to, label, external }) => (
            <li key={to}>
              {external ? (
                <a href={to} target="_blank" rel="noopener noreferrer">
                  {label}
                </a>
              ) : (
                <Link
                  to={to}
                  className={location.pathname === to ? 'active' : ''}
                >
                  {label}
                </Link>
              )}
            </li>
          ))}
          <li>
            <EventsDropdown variant="desktop" />
          </li>
          {NAV_LINKS_SECONDARY.map(({ to, label, external }) => (
            <li key={to}>
              {external ? (
                <a href={to} target="_blank" rel="noopener noreferrer">
                  {label}
                </a>
              ) : (
                <Link
                  to={to}
                  className={location.pathname === to ? 'active' : ''}
                >
                  {label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Mobile hamburger */}
        <button
          className={`navbar__hamburger${isMenuOpen ? ' open' : ''}`}
          onClick={() => setIsMenuOpen(v => !v)}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <span />
          <span />
          <span />
        </button>
      </nav>

      {/* Mobile full-screen overlay — the hamburger (fixed above this overlay
          in z-index so it stays visible and tappable) already animates into
          an X when open, so it doubles as this overlay's close control. */}
      <div
        className={`navbar__overlay${isMenuOpen ? ' open' : ''}`}
        aria-hidden={!isMenuOpen}
      >
        {NAV_LINKS_PRIMARY.map(({ to, label, external }) =>
          external ? (
            <a key={to} href={to} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          ) : (
            <Link key={to} to={to}>
              {label}
            </Link>
          )
        )}
        <EventsDropdown variant="mobile" />
        {NAV_LINKS_SECONDARY.map(({ to, label, external }) =>
          external ? (
            <a key={to} href={to} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          ) : (
            <Link key={to} to={to}>
              {label}
            </Link>
          )
        )}
      </div>

      {/* WhatsApp floating button — visible on all pages */}
      <a
        href="https://wa.me/6590079953?text=Hi%20Lebua%20Thai%2C%20I%27d%20like%20to%20reserve%20a%20table."
        className="whatsapp-fab"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
      >
        <FontAwesomeIcon icon={faWhatsapp} />
      </a>
    </>
  );
};

export default NavigationBar;
